import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import banner from "../assets/banner.jpg";
import logo from "../assets/logo.jpg";
import cow from "../assets/cow.mp4";
import { getCaptcha, submitWhitelist } from "../lib/whitelist.functions";

const X_URL = "https://x.com/Arc_Cows";
const X_POST_URL = "https://x.com/Arc_Cows/status/2079908148949708938?s=20";

function Cloud({ top, delay, fast }: { top: string; delay: string; fast?: boolean }) {
  return (
    <div
      className={`absolute ${fast ? "anim-drift-fast" : "anim-drift-slow"} pointer-events-none`}
      style={{ top, animationDelay: delay, left: 0, right: 0 }}
    >
      <div className="w-24 h-8 bg-white rounded-sm opacity-90 relative">
        <div className="absolute -top-2 left-3 w-6 h-6 bg-white rounded-sm" />
        <div className="absolute -top-3 left-10 w-8 h-8 bg-white rounded-sm" />
        <div className="absolute -top-2 right-3 w-6 h-6 bg-white rounded-sm" />
      </div>
    </div>
  );
}

function Star({ left, top, delay }: { left: string; top: string; delay: string }) {
  return (
    <div
      className="absolute w-1 h-1 bg-white anim-blink"
      style={{ left, top, animationDelay: delay }}
    />
  );
}

function House({ x }: { x: string }) {
  return (
    <div className="absolute bottom-24" style={{ left: x }}>
      <div className="relative">
        {/* Roof */}
        <div className="w-0 h-0 mx-auto" style={{ borderLeft: "28px solid transparent", borderRight: "28px solid transparent", borderBottom: "22px solid #8b3a2a" }} />
        {/* Body */}
        <div className="w-14 h-14 bg-[#d9a86a] border-4 border-[#5a3a1c] relative">
          <div className="absolute top-1 left-1 w-3 h-3 bg-farm-sun border-2 border-[#5a3a1c] anim-flicker" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-6 bg-[#5a3a1c]" />
        </div>
      </div>
    </div>
  );
}

function Fence({ x }: { x: string }) {
  return (
    <div className="absolute bottom-20 flex gap-1" style={{ left: x }}>
      {[0,1,2,3].map((i) => (
        <div key={i} className="w-2 h-8 bg-[#c9a06a] border border-[#5a3a1c]" />
      ))}
    </div>
  );
}

type Captcha = { question: string; token: string } | null;

export function ArcCowsHome() {
  const [followed, setFollowed] = useState(false);
  const [liked, setLiked] = useState(false);
  const [retweeted, setRetweeted] = useState(false);
  const [replyLink, setReplyLink] = useState("");
  const [wallet, setWallet] = useState("");
  const [captcha, setCaptcha] = useState<Captcha>(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const loadCaptcha = async () => {
    try {
      const c = await getCaptcha();
      setCaptcha(c);
      setAnswer("");
    } catch {
      toast.error("Could not load captcha");
    }
  };

  useEffect(() => { loadCaptcha(); }, []);

  const progress = [followed, liked, retweeted, replyLink.length > 10, wallet.length > 20].filter(Boolean).length;
  const total = 5;

  const openTaskX = (setter: (v: boolean) => void, url: string) => {
    window.open(url, "_blank", "noopener");
    setter(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captcha) return;
    setSubmitting(true);
    try {
      await submitWhitelist({
        followed, liked, retweeted,
        reply_link: replyLink,
        wallet_address: wallet,
        captcha_token: captcha.token,
        captcha_answer: answer,
      });
      setDone(true);
      toast.success("Welcome to the herd! 🐄");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Submission failed";
      toast.error(msg);
      loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen farm-sky text-farm-cream relative overflow-hidden">
      <Toaster position="top-center" toastOptions={{ className: "font-pixel-body !text-lg" }} />

      {/* Stars */}
      <Star left="8%" top="6%" delay="0s" />
      <Star left="22%" top="12%" delay="0.4s" />
      <Star left="45%" top="4%" delay="1.1s" />
      <Star left="65%" top="9%" delay="0.8s" />
      <Star left="82%" top="14%" delay="1.6s" />
      <Star left="92%" top="6%" delay="0.2s" />

      {/* Drifting clouds */}
      <Cloud top="10%" delay="0s" />
      <Cloud top="18%" delay="-20s" fast />
      <Cloud top="26%" delay="-40s" />

      {/* Moon / Sun */}
      <div className="absolute top-8 right-10 w-16 h-16 bg-farm-sun anim-float"
           style={{ boxShadow: "0 0 0 4px #2a1a0a, 0 0 40px #ffcf40aa", imageRendering: "pixelated" }} />

      {/* NAV */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Arc Cows logo" className="w-12 h-12 border-4 border-farm-cream" />
          <span className="font-pixel text-sm md:text-base tracking-widest">ARC COWS</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="#whitelist" className="hidden md:inline font-pixel text-[10px] hover:text-farm-sun">WHITELIST</a>
          <a href="#about" className="hidden md:inline font-pixel text-[10px] hover:text-farm-sun">ABOUT</a>
          <a href={X_URL} target="_blank" rel="noopener noreferrer" className="pixel-btn !py-2 !px-3 !text-[10px]">FOLLOW X</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative z-10 px-4 md:px-12 pt-6 pb-10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div className="anim-pop-in">
            <h1 className="font-pixel text-2xl md:text-4xl leading-[1.6] text-farm-cream">
              A PIXEL HERD<br />
              <span className="text-farm-sun">GRAZING</span> ON-CHAIN
            </h1>
            <p className="mt-6 font-pixel-body text-xl md:text-2xl text-farm-cream/90 max-w-lg">
              Welcome to the Arc Cows farm. It's a pixel-art NFT collection
              living on Arc Chain which have some plans TBA. Milk the tasks
              below to claim your whitelist spot if you are lucky.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#whitelist" className="pixel-btn">JOIN WHITELIST</a>
              <a href={X_URL} target="_blank" rel="noopener noreferrer" className="pixel-btn" style={{ background: "#fff4d6" }}>@ARC_COWS</a>
            </div>
          </div>
          <div className="relative anim-bob">
            <img src={banner} alt="Arc Cows banner — pixel cows on a starlit meadow with a wooden sign" className="w-full border-4 border-farm-cream" />
          </div>
        </div>
      </header>

      {/* ABOUT / FEATURES */}
      <section id="about" className="relative z-10 px-4 md:px-12 py-14">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-pixel text-lg md:text-2xl text-center text-farm-cream mb-10">MEET THE HERD</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="pixel-card p-6 anim-pop-in">
              <div className="w-full aspect-square overflow-hidden border-4 border-[#2a1a0a] mb-4">
                <video src={cow} aria-label="Arc Cows animated pixel NFT" autoPlay loop muted playsInline className="w-full h-full object-cover" />
              </div>
              <h3 className="font-pixel text-xs mb-2">PIXEL ART</h3>
              <p className="font-pixel-body text-lg">Hand-crafted pixelated 85 rarity traits cows with Sunglasses, Skin, Clothes and eyes.</p>
            </div>
            <div className="pixel-card p-6 anim-pop-in" style={{ animationDelay: "0.1s" }}>
              <div className="w-full aspect-square overflow-hidden border-4 border-[#2a1a0a] mb-4 relative bg-[#2a1a0a]">
                <video src={cow} aria-label="Locked cozy farm cow" autoPlay loop muted playsInline className="w-full h-full object-cover blur-md scale-110 opacity-60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
                  <div className="font-pixel text-4xl text-farm-sun mb-2">🔒</div>
                  <div className="font-pixel text-[10px] text-farm-cream">LOCKED</div>
                </div>
              </div>
              <h3 className="font-pixel text-xs mb-2">COZY FARM</h3>
              <p className="font-pixel-body text-lg">Every cow lives in a pixel farm — barns, meadows, starry nights. Just vibes.</p>
            </div>
            <div className="pixel-card p-6 anim-pop-in" style={{ animationDelay: "0.2s" }}>
              <div className="w-full aspect-square mb-4 border-4 border-[#2a1a0a] bg-farm-sun flex items-center justify-center">
                <div className="font-pixel text-2xl text-[#2a1a0a]">CowList</div>
              </div>
              <h3 className="font-pixel text-xs mb-2">SECURE WL</h3>
              <p className="font-pixel-body text-lg">Server-verified whitelist with anti-bot math captcha. No bots on the farm.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHITELIST */}
      <section id="whitelist" className="relative z-10 px-4 md:px-12 py-16">
        <div className="max-w-3xl mx-auto pixel-card p-6 md:p-10">
          <h2 className="font-pixel text-lg md:text-xl mb-2">SECURE WHITELIST</h2>
          <p className="font-pixel-body text-lg mb-6">
            Complete every task, tag 3 fren cows, drop your wallet and prove you're human. Herd-first, bots eat hay.
          </p>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between font-pixel text-[10px] mb-2">
              <span>PROGRESS</span><span>{progress}/{total}</span>
            </div>
            <div className="w-full h-4 border-4 border-[#2a1a0a] bg-white">
              <div className="h-full bg-farm-grass transition-all" style={{ width: `${(progress / total) * 100}%` }} />
            </div>
          </div>

          {done ? (
            <div className="text-center py-8 anim-pop-in">
              <video src={cow} aria-label="cow" autoPlay loop muted playsInline className="w-32 h-32 mx-auto mb-4 border-4 border-[#2a1a0a]" />
              <h3 className="font-pixel text-lg mb-2">MOO-VELOUS!</h3>
              <p className="font-pixel-body text-xl">You're in the herd. See you at mint.</p>
            </div>
          ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            {/* Task 1 */}
            <div className="flex items-center justify-between gap-3 border-4 border-[#2a1a0a] p-3 bg-white/60">
              <span className="font-pixel text-[10px]">1. FOLLOW @ARC_COWS</span>
              <button type="button" onClick={() => openTaskX(setFollowed, X_URL)}
                className="pixel-btn !py-2 !px-3 !text-[10px]" style={{ background: followed ? "#6ab04c" : "#ffcf40" }}>
                {followed ? "✓ DONE" : "FOLLOW"}
              </button>
            </div>
            {/* Task 2 */}
            <div className="flex items-center justify-between gap-3 border-4 border-[#2a1a0a] p-3 bg-white/60">
              <span className="font-pixel text-[10px]">2. LIKE THE POST</span>
              <button type="button" onClick={() => openTaskX(setLiked, X_POST_URL)}
                className="pixel-btn !py-2 !px-3 !text-[10px]" style={{ background: liked ? "#6ab04c" : "#ffcf40" }}>
                {liked ? "✓ DONE" : "LIKE"}
              </button>
            </div>
            {/* Task 3 */}
            <div className="flex items-center justify-between gap-3 border-4 border-[#2a1a0a] p-3 bg-white/60">
              <span className="font-pixel text-[10px]">3. RT + TAG 3 FRENS (REPLY)</span>
              <button type="button" onClick={() => openTaskX(setRetweeted, X_POST_URL)}
                className="pixel-btn !py-2 !px-3 !text-[10px]" style={{ background: retweeted ? "#6ab04c" : "#ffcf40" }}>
                {retweeted ? "✓ DONE" : "RETWEET"}
              </button>
            </div>

            {/* Reply link */}
            <div>
              <label className="font-pixel text-[10px] block mb-1">YOUR REPLY LINK (RT + 3 FRENS TAGGED)</label>
              <input
                className="pixel-input"
                placeholder="https://x.com/yourhandle/status/..."
                value={replyLink}
                onChange={(e) => setReplyLink(e.target.value)}
                maxLength={300}
                required
              />
              <p className="font-pixel-body text-base mt-1 opacity-80">
                Paste the link to your reply on{" "}
                <a href={X_POST_URL} target="_blank" rel="noopener noreferrer" className="underline">this post</a>.
              </p>
            </div>

            {/* Wallet */}
            <div>
              <label className="font-pixel text-[10px] block mb-1">WALLET ADDRESS</label>
              <input
                className="pixel-input"
                placeholder="0x... EVM address"
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                pattern="0x[a-fA-F0-9]{40}"
                title="Enter a valid EVM address (0x followed by 40 hex characters)"
                maxLength={42}
                required
              />
              <p className="font-pixel-body text-base mt-1 opacity-80">EVM only — must start with 0x (42 chars).</p>
            </div>

            {/* Captcha */}
            <div className="border-4 border-dashed border-[#2a1a0a] p-4 bg-farm-sun/40">
              <label className="font-pixel text-[10px] block mb-2">🤖 HUMAN CHECK</label>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-pixel text-base bg-white border-4 border-[#2a1a0a] px-3 py-2">
                  {captcha ? `${captcha.question} = ?` : "..."}
                </span>
                <input className="pixel-input !w-24" inputMode="numeric" value={answer} onChange={(e) => setAnswer(e.target.value)} required />
                <button type="button" onClick={loadCaptcha} className="pixel-btn !py-2 !px-3 !text-[10px]" style={{ background: "#fff4d6" }}>NEW</button>
              </div>
            </div>

            <button type="submit" disabled={submitting} className="pixel-btn w-full !py-4">
              {submitting ? "SUBMITTING..." : "SUBMIT TO HERD"}
            </button>
          </form>
          )}
        </div>
      </section>

      {/* GROUND SCENE */}
      <div className="relative farm-ground h-40 mt-8 overflow-hidden">
        <House x="8%" />
        <Fence x="26%" />
        <House x="70%" />
        <Fence x="88%" />
      </div>

      {/* FOOTER */}
      <footer className="relative z-10 px-6 py-8 farm-ground border-t-4 border-[#2a4a1a]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-pixel text-[10px] text-white">© ARC COWS · MOO'D FOREVER</span>
          <a href={X_URL} target="_blank" rel="noopener noreferrer" className="font-pixel text-[10px] text-white hover:text-farm-sun">@ARC_COWS ON X</a>
        </div>
      </footer>
    </div>
  );
}
